{abs, ceil, floor, max, min, round} := Math
{assign} := Object
import * as PIXI from "pixi.js"

{ MidiEvent, clamp } from @danielx/lib

type { Tool } from ./tools.civet
type { SpriteTexture } from ./lib/spritesheet.civet

{
  Container
  Graphics
  NineSlicePlane
  ObservablePoint
  Point
  Rectangle
  Renderer
  RenderTexture
  Sprite
  Text
  Texture
} from "pixi.js"

{
  CHANNEL_COLORS as ChannelColors
  FFT_SIZE
  FINGERING_COLORS
  JUDGMENT_COLORS
  JUDGMENT_FEEDBACK_MS
  JUDGMENT_LABELS
  PIXELS_PER_BEAT
  SAMPLE_RATE
  SPRITE_SIZE
  VIEW_KEY
  MODE_VIEWS_KEY
} from ./const.civet

{ fingeringDisplay } from ./lib/midi/practice-metadata.civet
{ buildNoteIndex } from ./lib/midi/note-index.civet
{ GameScore } from ./lib/midi/game-score.civet
{ readPracticeMetadata, practiceChannels } from ./lib/midi/practice-metadata.civet
{ autoPracticeMetadata } from ./lib/midi/auto-practice.civet
{ BURST_MAX_AGE_MS, BurstLayer, spawnJudgedBurst } from ./note-fx.civet
{ JudgmentFx, setArcadeBeat } from ./arcade-fx.civet

PianoRoll from ./piano-roll.civet
HorizontalRoll from ./horizontal-roll.civet
GameHudLayer from ./game-hud-layer.civet

{ audioBufferToWav } from ./lib/encoder/index.civet

type { PlayerAudioInstance } from "./player-audio.civet"

// if we're in the Electron app then use the ipcRenderer to post png blobs to
// create the mp4 video
let ipcRenderer: any
if global.ELECTRON_VERSION
  {ipcRenderer} = global.require('electron')

// Vertical pixel spacing between two staff lines
STAFF_SPACING := 48 // px
// horizontal width of ledger lines added when notes are beyond staff
LEDGER_LINE_WIDTH := 48 // px

FPS := 60

// How many rendered frames may be in flight to ffmpeg at once. Enough to keep the encoder
// fed while the next frame draws; small enough that the queue can't grow into real memory
// (a 1440x2560 RGBA frame is 14.7MB).
MAX_FRAMES_IN_FLIGHT := 4

THEMES :=
  light:
    staffAlpha: 0.9375
    lyricsColor: 0x202020

  dark:
    staffAlpha: 0.125
    lyricsColor: 0xffffff

{
  Accidental
  channelNoteKey
  midiKeySignatureName
  midiPitchToStaffNote
  noteName
  quantize
  staffNoteToMidiPitch
} from ./lib/util.civet

{ presetByName, presetLoaded } from ./lib/shaders.civet
{ phaseLock } from ./lib/phase-lock.civet

{ Modal, Observable, Progress } from @danielx/dui

PIXI.utils.skipHello()

// Center by adjusting x coordinate because using 0.5 anchor point wobbles
// when sprite has non-integer width
centerSprite := (s: Sprite) ->
  s.x = floor s.x - s.width/2

ResourcePool := (maxSize=256, constructor: => Sprite) ->
  poolIndex .= 0
  pool: Sprite[] := []
  i .= 0
  while i < maxSize
    pool.push constructor()
    i++

  reset: ->
    poolIndex = 0
    pool.forEach (s) ->
      s.visible = false
  getInstance: ->
    let s
    if poolIndex < maxSize
      s = pool[poolIndex++]
      s.visible = true

    return s

// Return sprites for text creating and re-using textures as
// maxSize is only for sprites, textures is unlimited
TextPool := (maxSize=256, textStyle?: Partial<PIXI.ITextStyle>) ->
  poolIndex .= 0
  pool: Sprite[] := []
  i .= 0

  textStyle ?=
    fontFamily: "Fira Sans"
    fontSize: 28
    fill: 0xFFFFFF

  textures := new Map

  while i < maxSize
    pool.push assign new Sprite(),
      tint: 0x000000
    i++

  preload := new Text "preload", textStyle

  reset: ->
    poolIndex = 0
    pool.forEach (s) ->
      s.visible = false

  getTextSprite: (text: string) ->
    if !document.fonts.check("28px 'Fira Sans'")
      preload.updateText(false)
      return

    let s
    if poolIndex < maxSize
      s = pool[poolIndex++]

      texture .= textures.get(text)

      if !texture
        t := new Text text, textStyle
        t.updateText(false)

        texture = t.texture
        textures.set(text, texture)

      s.texture = texture
      s.visible = true

    return s

BorderSlice := do
  canvas := document.createElement 'canvas'
  canvas.width = canvas.height = 3
  ctx := canvas.getContext('2d')!
  ctx.fillStyle = "#ffffff"
  ctx.fillRect(0, 0, 3, 3)
  ctx.clearRect(1, 1, 1, 1)

  Texture.from canvas

// A white bar with a vertical sheen gradient and gently rounded ends;
// tinted per channel and nine-sliced horizontally so the corners never
// stretch
DURATION_BAR_HEIGHT := STAFF_SPACING / 4
DURATION_BAR_RADIUS := 2
DurationBarSlice := do
  h := DURATION_BAR_HEIGHT
  canvas := document.createElement 'canvas'
  canvas.width = h * 2
  canvas.height = h
  ctx := canvas.getContext('2d')!

  gradient := ctx.createLinearGradient(0, 0, 0, h)
  gradient.addColorStop 0, "#FFFFFF"
  gradient.addColorStop 0.45, "#E2E2E2"
  gradient.addColorStop 1, "#A8A8A8"
  ctx.fillStyle = gradient
  ctx.beginPath()
  ctx.roundRect 0, 0, h * 2, h, DURATION_BAR_RADIUS
  ctx.fill()

  Texture.from canvas

// Soft radial dot for the travelling highlight riding a sounding bar
SheenTexture := do
  size := 32
  canvas := document.createElement 'canvas'
  canvas.width = canvas.height = size
  ctx := canvas.getContext('2d')!

  gradient := ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2)
  gradient.addColorStop 0, "rgba(255, 255, 255, 0.9)"
  gradient.addColorStop 0.6, "rgba(255, 255, 255, 0.35)"
  gradient.addColorStop 1, "rgba(255, 255, 255, 0)"
  ctx.fillStyle = gradient
  ctx.fillRect 0, 0, size, size

  Texture.from canvas

// Like ResourcePool but for the NineSlicePlane duration bars (no anchor:
// callers position by top-left)
DurationBarPool := (maxSize: number) ->
  poolIndex .= 0
  pool: NineSlicePlane[] := []
  i .= 0
  while i < maxSize
    bar := new NineSlicePlane DurationBarSlice, DURATION_BAR_RADIUS, 0, DURATION_BAR_RADIUS, 0
    bar.height = DURATION_BAR_HEIGHT
    bar.alpha = 0.875
    pool.push bar
    i++

  reset: ->
    poolIndex = 0
    pool.forEach (s) ->
      s.visible = false
  getInstance: ->
    let s
    if poolIndex < maxSize
      s = pool[poolIndex++]
      s.visible = true

    return s

type MeasureNumberSprite = Sprite & {children: [Sprite, Sprite, Sprite, Sprite]}

MeasureNumberPool := (maxSize: number) ->
  pool: MeasureNumberSprite[] := []
  poolIndex .= 0

  while poolIndex < maxSize
    poolIndex++

    border := new NineSlicePlane BorderSlice, 1.5, 1.5, 1.5, 1.5
    border.x = -2
    border.tint = 0x000000

    bg := new Sprite Texture.WHITE
    bg.x = -2

    shadow := new Sprite Texture.WHITE
    shadow.x = -1
    shadow.y = 1
    shadow.tint = 0
    shadow.alpha = 0.5

    text := new Sprite

    s := new Sprite()

    s.addChild shadow
    s.addChild bg
    s.addChild border
    s.addChild text

    pool.push s as MeasureNumberSprite

  textStyle: Partial<PIXI.ITextStyle> :=
    fontFamily: "Chicago"
    fontSize: 16
    fontStyle: "italic"
    fill: 0x241440

  textures := new Map

  updateBorder := (s: MeasureNumberSprite) ->
    [shadow, bg, border, text] := s.children

    shadow.width = bg.width = border.width = text.width + 7
    shadow.height = bg.height = border.height = text.height + 2

  reset: ->
    poolIndex = 0
    pool.forEach (s) ->
      s.visible = false

  getInstance: (text: string) ->
    return unless document.fonts.check("16px Chicago")

    let s
    if poolIndex < maxSize
      s = pool[poolIndex++]

      texture .= textures.get(text)

      if !texture
        t := new Text text, textStyle
        t.updateText(false)

        texture = t.texture
        textures.set(text, texture)

      s.children[3].texture = texture
      updateBorder(s)
      s.visible = true

    return s

MINIMAP_HEIGHT .= 128

zeroPoint := new Point 0, 0 |> as ObservablePoint
center := new Point 0.5, 0.5 |> as ObservablePoint
verticalCenter := new Point 0, 0.5 |> as ObservablePoint

// 1d overlap
overlap := (aStart: number, aEnd: number, bStart: number, bEnd: number) ->
  ahw := (aEnd - aStart) / 2
  bhw := (bEnd - bStart) / 2

  abs((bStart + bhw) - (aStart + ahw)) < ahw + bhw

velocityTextures := do
  width := 4
  height := 128

  canvas := document.createElement 'canvas'
  canvas.width = width
  canvas.height = height

  context := canvas.getContext('2d')!

  gradient := context.createLinearGradient(0, height, 0, 0)

  gradient.addColorStop(0.00, "#008000")
  gradient.addColorStop(0.50, "#00FF00")
  gradient.addColorStop(0.75, "#FFFF00")
  gradient.addColorStop(1.00, "#FF0000")

  context.fillStyle = gradient
  context.fillRect(0, 0, width, height)

  base := Sprite.from(canvas).texture

  [0..127].map (y) ->
    //@ts-ignore
    new Texture base, new Rectangle 0, height - y, width, y

// A live 3D backdrop for a video render. The scene owns its canvas and GL context
// (three.js or anything else); the renderer pulls one frame per video frame with exact
// musical time and the offline mix's analyser, and composites the canvas under the staff
// in the same slot the vfx shader backdrop uses. The contract mirrors the offline loop
// renderers in scripts/social (drive-page's drawFrame): deterministic given its inputs —
// no clocks, no unseeded randomness — because frames must be reproducible.
export type RenderScene = {
  // The scene's own canvas, sized by the caller at the OUTPUT (device) pixel size.
  canvas: HTMLCanvasElement
  // Called once per video frame, in frame order. `time`/`beat` are the song's musical
  // position (the same values the staff and vfx uniforms get); `analyser` is the offline
  // mix's stereo pair — call getByte{Frequency,TimeDomain}Data on .l/.r for
  // audio-reactive input, sampled at exactly this frame's time.
  drawFrame: (input: {
    frame: number
    time: number
    beat: number
    loopBeats: number
    analyser: { l: StereoAnalyserChannel, r: StereoAnalyserChannel }
  }) => void
  dispose?: () => void
}

type StereoAnalyserChannel = {
  frequencyBinCount: number
  getByteFrequencyData: (out: Uint8Array) => void
  getByteTimeDomainData: (out: Uint8Array) => void
}

/**
The analyser a render hands the shaders, with the render's gain trim taken back out of the
waveform.

The offline analyser taps the mix AFTER the trim (player-audio: fx network, then analyser,
then limiter), so a render trimmed by -24 dB gives the shaders a waveform at a sixteenth of
the level the app shows live. getByteTimeDomainData quantises that to 8 bits around 128,
which leaves a loud passage a few levels either side of centre, and a scope gained back up
draws it as a staircase. Reading floats and undoing the trim before quantising gives a
render the same full-resolution waveform playback shows.

The spectrum is passed through untouched: its bytes are dB-scaled, so a trim shifts them
without costing resolution, and the shaders reading it are tuned against trimmed renders.
*/
untrimmedAnalyser := <T extends { l: AnalyserNode, r: AnalyserNode, scope?: { l: AnalyserNode, r: AnalyserNode } }>(dest: T, gain: number): T ->
  return dest if gain is 1
  makeup := 1 / gain
  channel := (a: AnalyserNode) ->
    floats .= new Float32Array 0
    fftSize: a.fftSize
    frequencyBinCount: a.frequencyBinCount
    getByteFrequencyData: (out: Uint8Array) -> a.getByteFrequencyData out
    getFloatTimeDomainData: (out: Float32Array) ->
      a.getFloatTimeDomainData out
      for i of [0...out.length]
        out[i] *= makeup
      return
    getByteTimeDomainData: (out: Uint8Array) ->
      floats = new Float32Array out.length unless floats.length is out.length
      a.getFloatTimeDomainData floats
      // The byte mapping getByteTimeDomainData itself uses: 128 is silence, 128 * (1 + v).
      for i of [0...out.length]
        out[i] = Math.max 0, Math.min 255, Math.round 128 * (1 + floats[i] * makeup)
      return
  scope := dest.scope and { l: channel(dest.scope.l), r: channel(dest.scope.r) }
  { ...dest, l: channel(dest.l), r: channel(dest.r), scope } as any as T

type RenderVideoOptions = Partial<{
  fps: number
  startTime: number
  duration: number
  width: number
  height: number
  upscale: number
  // When set, the finished mp4 is written straight here and the save dialog is
  // skipped. Unattended renders (scripts/social) have no one to click it.
  outputPath: string
  // Suppress the progress modal — an offscreen render has no viewer.
  silent: boolean
  // Pipe raw RGBA to ffmpeg via a synchronous readback instead of async PNG blobs.
  // Defaults to `silent`, because a hidden renderer is exactly where toBlob is throttled.
  raw: boolean
  // Linear master gain ahead of the limiter — headroom for hot soundfonts. See
  // renderOfflineContext; 1 leaves the signal untouched.
  gain: number
  // Accumulate per-stage frame timings into global.__probe.
  probe: boolean
  // Transparent background: no backdrop sprite, no vfx filter, and ffmpeg.js encodes
  // with an alpha channel (qtrle .mov). For compositing plates — unlike a chroma key,
  // sprite art that happens to share the key color survives.
  alpha: boolean
  // Alpha encode: "qtrle" (default, lossless .mov) or "vp9" (yuva420p .webm — the
  // practical choice at UHD, where qtrle runs to tens of GB).
  alphaCodec: string
  // Show tempo/key/time-signature labels, measure numbers, and other textual staff
  // annotations. Defaults OFF for renders — the markings are editing aids, and in a
  // video they read as clutter. Lyrics always draw.
  staffText: boolean
  // The clip is going to be played on repeat (a social short, a background loop), so
  // join it to itself: the decay past the end is folded onto the beginning instead of
  // being cut off. Off for an ordinary export, where the last notes should ring out and
  // stop rather than reappear at the top. Also wraps the staff into a cylinder, so it has
  // no visible edge at the seam either — see the lap loop in render().
  loop: boolean
  // Device pixels per layout pixel. `width`/`height` stay the OUTPUT size; the staff is
  // laid out at width/scale × height/scale and drawn at `scale`, so raising it makes the
  // notes bigger rather than showing more of the song — the staff is a fixed pixel height,
  // which is why a bigger frame alone only buys margin.
  //
  // Live backdrop scene — NOT JSON-serialisable, so a CLI render constructs it in the
  // page (render-cli --scene bundles the module and window.require()s it) rather than
  // passing it through executeJavaScript's argument. See RenderScene above.
  scene: RenderScene
  // This is the native version of the `upscale` flag, and the two differ in what gets to
  // be sharp. ffmpeg's nearest 2x enlarges the finished frame, so everything in it is
  // equally chunky. Rendering at `scale` re-draws vector geometry and runs the vfx shaders
  // at the higher resolution while pixel-art sprite textures still sample NEAREST, so the
  // art stays chunky and everything around it gets the detail. Text is the exception: PIXI
  // rasterises it at `settings.RESOLUTION`, so it scales up chunky like the sprites.
  scale: number
  // Multiplier on the sprite pools' caps — see CameraOptions.poolScale. Defaults to 4 for
  // a render: an undersized pool drops notes and they pop in as the staff scrolls, and the
  // only cost of headroom here is wall time, which offline renders can spend. The editor
  // draws with 1, where the same headroom would be paid per frame at 60fps.
  poolScale: number
  // Which view draws the frames: the staff Camera (default) or the vertical
  // piano roll ("roll" — falling notes landing on a keyboard).
  view: "staff" | "roll"
  // Roll view only: simulate a flawless Game Mode pass. Every note on an
  // active channel lands a perfect exactly at its start time — score, combo,
  // health, judgment popups, bursts, and the arcade HUD all ride along.
  autoplay: boolean
  // The practiced part for autoplay, as 0-based MIDI channels — overrides the
  // song's practice metadata and the melody auto-pick, for songs where the
  // analysis grabs the wrong voice.
  partChannels: number[]
  // Shift the roll's beat/measure grid this many beats later, so a song with
  // a pickup draws its lines on the audible downbeats instead of tick zero's.
  beatOffset: number
}>

type MidiSong = import('./lib/midi/song.civet').default

// TODO: Figure out circular mixin deps
interface PlayerMethods
  autoscroll: () => number
  on: (event: string, listener: (...args: any[]) => void) => void
  song: MidiSong

StaffRenderer := <T extends Constructor<PlayerAudioInstance>>(S: T) ->
  abstract class Renderer extends S
    abstract autoscroll: () => number

    panUp: () => void
    panDown: () => void
    resize: () => void
    screenPositionToBeatNote: (x: number, y: number, event: MouseEvent) => {beat: number, note: number}
    theaterMode: () => void
    createPatternPreview: (pattern: Pattern) => HTMLCanvasElement
    beatNoteToScreenPosition: ({beat, note}: {beat: number, note: number}) => {x: number, y: number}

    render: () => void
    renderVideo: (options?: RenderVideoOptions) => Promise<string | undefined>

    // Which view fills the viewport: "staff" (default), "roll" (the vertical
    // falling-note view), or "hroll" (the horizontal DAW-style roll)
    viewMode: import('@danielx/observable').ObservableValue<string>
    togglePianoRoll: () => void
    // Each play mode remembers its own view; setModeView applies it live
    // when `mode` is the current one (see views/mode-modal.civet)
    modeView: (mode: string) => string
    setModeView: (mode: string, view: string) => void

    viewportElement: () => HTMLElement
    viewportOffset: () => {x: number, y: number}

    initRenderer: () => void

    constructor(...args: any[])
      super(...args)

      assign PIXI.settings,
        ROUND_PIXELS: true
        SCALE_MODE: PIXI.SCALE_MODES.NEAREST

      app := new PIXI.Application
        width: 640
        height: 480
        backgroundColor: 0xEDE7F6
        // MSAA for vector geometry: without it small corner radii (the roll's
        // falling notes) rasterize away asymmetrically at resolution 1.
        // Texture sampling stays NEAREST, so sprite art is untouched.
        antialias: true

      global.app = app

      // Take more control of render step
      //@ts-ignore
      app._ticker.remove(app.render, app)

      resizeHandler := ->
        rect := viewportElement.getBoundingClientRect()
        {width, height} := rect
        screenHalfSize.set(width/2, height/2)
      window.addEventListener "resize", resizeHandler
      setTimeout resizeHandler, 0

      let camera: ReturnType<typeof Camera>, shw: number, shh: number
      let pianoRoll: ReturnType<typeof PianoRoll> | undefined
      let horizontalRoll: ReturnType<typeof HorizontalRoll> | undefined
      let gameHud: ReturnType<typeof GameHudLayer> | undefined
      // Callback for when viewport size changes
      screenHalfSize := new ObservablePoint ->
        {x:shw, y: shh} = screenHalfSize

        camera.resize()
        pianoRoll?.resize()
        horizontalRoll?.resize()
        app.renderer.resize(shw * 2, shh * 2)
      , @, 768, 1024

      savedView .= "staff"
      try savedView = localStorage.getItem(VIEW_KEY) or "staff"
      @viewMode = Observable savedView

      // Per-mode view preference, seeded from the single saved view so an
      // existing choice carries over. Persisted as one JSON map.
      modeViews: Record<string, string> := {edit: savedView, practice: savedView, game: savedView}
      try
        if savedModes := localStorage.getItem MODE_VIEWS_KEY
          Object.assign modeViews, JSON.parse savedModes
      modeViewsChanged := Observable 0

      @modeView = (mode) =>
        modeViewsChanged()
        modeViews[mode]

      @setModeView = (mode, view) =>
        modeViews[mode] = view
        modeViewsChanged.increment()
        try
          localStorage.setItem MODE_VIEWS_KEY, JSON.stringify modeViews
        @viewMode view if (@ as! Player).playMode() is mode

      // Created on first use: most sessions never leave the staff
      updateStage := =>
        return unless camera

        switch @viewMode()
          "roll"
            unless pianoRoll
              pianoRoll = PianoRoll(screenHalfSize, @ as! Player)
              pianoRoll.songLoaded @song
            app.stage.removeChild camera.container
            if horizontalRoll
              app.stage.removeChild horizontalRoll.container
            app.stage.addChild pianoRoll.container
          "hroll"
            unless horizontalRoll
              horizontalRoll = HorizontalRoll(screenHalfSize, @ as! Player)
              horizontalRoll.songLoaded @song
            app.stage.removeChild camera.container
            if pianoRoll
              app.stage.removeChild pianoRoll.container
            app.stage.addChild horizontalRoll.container
          else
            if pianoRoll
              app.stage.removeChild pianoRoll.container
            if horizontalRoll
              app.stage.removeChild horizontalRoll.container
            app.stage.addChild camera.container

        // always last: the game HUD draws over whichever view is active
        if gameHud
          app.stage.addChild gameHud.container

      @viewMode.observe (v: string) =>
        try
          localStorage.setItem VIEW_KEY, v
        updateStage()

      @togglePianoRoll = =>
        // through setModeView so the flip sticks as the current mode's choice
        next := switch @viewMode()
          "staff" then "roll"
          "roll" then "hroll"
          else "staff"
        @setModeView (@ as! Player).playMode(), next

      viewportElement := document.createElement 'viewport'
      viewportElement.appendChild app.view

      viewportOffset :=
        x: 0
        y: 0

      @createPatternPreview = (pattern) =>
        camera.createPatternPreview(pattern)

      @viewportElement = ->
        viewportElement

      @viewportOffset = ->
        viewportOffset

      @panUp = ->
        viewportOffset.y -= STAFF_SPACING

      @panDown = ->
        viewportOffset.y += STAFF_SPACING

      @render = =>
        if camera
          autoscroll := @autoscroll()
          if autoscroll
            @adjustPlayhead autoscroll / FPS

          // cyclic FX clocks (wiggle, flames, HUD fluid) snap to this beat's
          // 32nd/64th grid
          setArcadeBeat @currentBeat()

          if @viewMode() is "roll" and pianoRoll
            pianoRoll.render(@song, @currentBeat())
          else if @viewMode() is "hroll" and horizontalRoll
            horizontalRoll.render(@song, @currentBeat())
          else
            camera.render(@song, @currentBeat())
          gameHud?.render performance.now()
          app.renderer.render(app.stage)

      @resize = ->
        camera.resize()
        pianoRoll?.resize()
        horizontalRoll?.resize()

      @screenPositionToBeatNote = (x, y, event) =>
        if @viewMode() is "roll" and pianoRoll
          pianoRoll.screenPositionToBeatNote(x, y, event)
        else if @viewMode() is "hroll" and horizontalRoll
          horizontalRoll.screenPositionToBeatNote(x, y, event)
        else
          camera.screenPositionToBeatNote(x, y, event)

      @theaterMode = ->
        // not the mode hud: it manages its own hidden class by play mode
        document.querySelectorAll('aside:not(.mode-hud), tools, pre.position').forEach (n) ->
          n.classList.toggle('hidden')

        resizeHandler()

      // Init animate loop and observers
      animate =>
        @render()

      @initRenderer = =>
        camera = Camera(screenHalfSize, @ as! Player, {renderer: app.renderer})
        gameHud = GameHudLayer(screenHalfSize, @ as! Player)
        updateStage()

        // Entering practice/game swaps to that mode's view and drops the edit
        // chrome (style.styl hides it under app.performing); the canvas takes
        // the freed space once the layout settles
        (@ as! Player).playMode.observe (m: string) =>
          @viewMode modeViews[m] ?? "staff"
          (@ as! Player).element.classList.toggle "performing", m is not "edit"
          setTimeout resizeHandler, 0

        @on "reset", =>
          camera.songLoaded(@song)
          pianoRoll?.songLoaded(@song)
          horizontalRoll?.songLoaded(@song)

      @beatNoteToScreenPosition = ({beat, note}) =>
        {x:shw, y:shh} := screenHalfSize
        {y:yOffset} := @viewportOffset()
        currentBeat := @currentBeat()
        {left:vx, top:vy} := viewportElement.getBoundingClientRect()

        keySignature := @song.keySignatureEventAtBeat(beat).key
        [staffNote] := midiPitchToStaffNote(note, keySignature)
        sw := shw * 2

        x: (beat - currentBeat) * PIXELS_PER_BEAT + 1 / 3 * sw
        y: shh - staffNote * STAFF_SPACING / 2 + vy - yOffset

      @renderVideo = (options: RenderVideoOptions={}) =>
        {fps, startTime, duration, width, height, upscale, outputPath, silent, raw, gain, loop: looping, scale, alpha, alphaCodec, staffText, scene, poolScale, view, autoplay, partChannels, beatOffset} .= options
        // Default the synchronous readback on for headless renders; nothing else has a
        // reason to pay for raw frames, and nothing else is throttled.
        raw ?= silent

        afxPresetName := player.afxPresetName()
        vfxPresetName := player.vfxPresetName()
        spriteScale := player.spriteScale()
        spriteTextures := player.spriteTextures()
        song := player.song
        secondsLength := song.secondsLength()

        title := song.title() or "render"

        renderId: number | null .= null

        startTime ?= 0

        width ?= 1280
        height ?= 720
        fps ?= 60
        duration ?= secondsLength
        duration = min duration, secondsLength - startTime

        // A clip is a MUSICAL length and video is a whole number of frames, and the two
        // almost never agree: Mushroom Forest is 43.6149s, which at 30fps is 1308.45
        // frames. Truncating to 1308 cuts 15ms out of the music, padding to 1309 adds
        // 18ms of nothing — either way a looping playback jumps a step at the seam, and
        // the cut lands mid-note, which is the click.
        //
        // So: keep every sample of the music and fit it to the frame grid instead.
        // Frames are stepped at duration/frameCount, and the mix is resampled by the
        // same ratio at mux time (audioRate below). The stretch is bounded by half a
        // frame over the whole clip — 0.04% at worst, 0.6 cents of pitch — and the seam
        // becomes sample-exact.
        frameCount := max 1, round duration * fps
        videoSeconds := frameCount / fps
        // Skip the filter entirely when the clip already lands on a frame (an exact
        // `--duration 40`), so the common case stays a straight copy.
        stretched := abs(videoSeconds - duration) > 1e-9

        // Long enough to bridge the step at the seam, short enough not to be heard as a
        // second passage over the opening. See foldLoopTail — 1.5s of it was very audible.
        loopTail := if looping then min 0.15, duration / 4 else 0

        scale ?= 1
        // Layout size. The canvas comes out at layout × scale, which is what the caller
        // asked for as width/height — rounded so a scale that doesn't divide evenly still
        // produces a whole number of device pixels rather than a fractional canvas.
        layoutWidth := round width / scale
        layoutHeight := round height / scale
        deviceWidth := round layoutWidth * scale
        deviceHeight := round layoutHeight * scale

        webmApp := new PIXI.Application
          width: layoutWidth
          height: layoutHeight
          resolution: scale
          backgroundColor: 0xEDE7F6
        //@ts-ignore
        webmApp._ticker.remove(webmApp.render, webmApp)

        progressView := Progress
          value: 0
          max: 1
          message: "Preparing Audio..."

        unless silent
          Modal.show progressView.element,
            cancellable: false

        let renderCam: ReturnType<typeof Camera>?
        let renderRoll: ReturnType<typeof PianoRoll>?
        let renderHud: ReturnType<typeof GameHudLayer>?

        // view "roll" + autoplay: a simulated flawless Game Mode pass. The
        // chart is every note on an active channel; doFrame feeds each one to
        // a real GameScore as a perfect the moment the clip reaches it, so
        // the HUD, combo tiers, and burst scaling behave exactly as a live
        // pass that never misses. fxClock is the synthetic now (ms since the
        // clip's start) the views read through fxNow — offline frames don't
        // advance in wall time.
        rollFeedback: {n: number, judgment: string, beat: number, at: number, tier: number}[] := []
        rollScore := new GameScore
        hudScore := Observable 0
        hudCombo := Observable 0
        hudMultiplier := Observable 1
        hudHealth := Observable 1
        fxClock .= 0
        // The practiced part, resolved the same way live entry does (enterMode
        // in practice.civet): the song's practice metadata, else the
        // structural analysis's melody pick. Only the part is judged and
        // drawn — backing channels play in the mix but never score or show on
        // the roll, exactly as in a live game pass.
        practiceMeta := if view is "roll" and autoplay and not partChannels?.length
          readPracticeMetadata(song) ?? autoPracticeMetadata(song)
        practiceSet := new Set<number> partChannels ?? (if practiceMeta then practiceChannels(practiceMeta) else [0...16])
        chart := if view is "roll" and autoplay
          buildNoteIndex(song).filter (note) => song.channelActive[note.c] and practiceSet.has note.c
        else []
        // notes before the clip's start count as already played: skipped
        // outright so the pass starts fresh at the first visible hit
        chartCursor .= 0
        chartCursor++ while chartCursor < chart.length and chart[chartCursor].startTime < startTime

        // TODO: need to add analyser attached to offline context for viz fx
        // frequency channels
        mockPlayer :=
          activePattern: ->
          analysedDestination:
            l:
              // This size is used to initialize the texture buffer for the vfx shader, so
              // it has to match what the REAL analyser reports — an AnalyserNode's
              // frequencyBinCount is fftSize/2, never fftSize. Declaring FFT_SIZE here made
              // the render's texture twice as wide as the data: getByteFrequencyData fills
              // min(buffer.length, frequencyBinCount) entries, so the upper half stayed at
              // zero for the whole render and every shader that samples iChannel0 above
              // x = 0.5 read silence. It never reproduced in the editor, where the live
              // analyser reports the right count.
              frequencyBinCount: FFT_SIZE / 2
              getByteFrequencyData: (_buffer: Uint8Array) =>
              getByteTimeDomainData: (_buffer: Uint8Array) =>
              getFloatTimeDomainData: (_buffer: Float32Array) =>
            r:
              frequencyBinCount: FFT_SIZE / 2
              getByteFrequencyData: (_buffer: Uint8Array) =>
              getByteTimeDomainData: (_buffer: Uint8Array) =>
              getFloatTimeDomainData: (_buffer: Float32Array) =>
          on: ->
          accidentalModifier: ->
            0
          quantize: ->
            0
          notePosition: ->
          playing: ->
            true
          // TODO: copy to a cache for the duration of the render
          spriteTextures: ->
            spriteTextures
          spriteScale: ->
            spriteScale
          song: song
          velocityToolActive: ->
            false
          afxPresetName: ->
            afxPresetName
          vfxPresetName: ->
            vfxPresetName
          viewportOffset: ->
            y: 0
            x: 0

        // The FIFO, when there is one. Node stream: write() returns false when its buffer
        // is full, and that backpressure is ffmpeg telling us it is behind.
        //
        // One 'drain' and one 'error' listener for the whole render, not one per frame —
        // several frames can be waiting at once, and per-write listeners blow through
        // Node's max-listeners warning within the first second.
        let frameStream: any
        let streamError: Error?
        drainWaiters: (() => void)[] := []
        releaseWaiters := ->
          drainWaiters.splice(0).forEach (resume) -> resume()

        writeFrame := (bytes: Uint8Array) ->
          // A dead stream means ffmpeg is gone; fail the render rather than block on a
          // 'drain' that will never come.
          return Promise.reject streamError if streamError

          new Promise<void> (resolve) ->
            if frameStream.write bytes
              resolve()
            else
              drainWaiters.push resolve

        // Frames handed to the main process but not yet confirmed written to ffmpeg.
        inFlight: Promise<unknown>[] := []

        // One more buffer than can be in flight, so the one being filled is never one the
        // main process is still reading.
        readBuffers := if raw
          [0...MAX_FRAMES_IN_FLIGHT + 1].map -> new Uint8Array deviceWidth * deviceHeight * 4
        else
          []
        readIndex .= 0

        // Counters for --probe. `wait` is everything outside this callback — the audio
        // context's suspend/resume round trip, which is the rest of the frame time.
        probe := options.probe and global.__probe ?= {frames: 0, draw: 0, ipc: 0, wait: 0, last: 0, render: 0, read: 0, mark: 0}

        frameTexture := raw and RenderTexture.create {
          width: layoutWidth, height: layoutHeight, resolution: scale
        }

        // Sprites read ROUND_PIXELS once, at construction — so this covers exactly the
        // pools this camera builds, and the interactive app's sprites keep their own value.
        //
        // Above scale 1 it has to be off. PIXI 6.1.2 rounds sprite vertices against the
        // global settings.RESOLUTION, not the renderer's, so it snaps them to whole layout
        // pixels no matter how fine the device grid is. With the scroll now snapped to
        // device pixels, leaving it on would round every other frame's half-pixel offset
        // the other way and reintroduce the jitter it exists to prevent.
        roundPixelsWas := PIXI.settings.ROUND_PIXELS
        PIXI.settings.ROUND_PIXELS = roundPixelsWas and scale <= 1

        try
          if view is "roll"
            rollPlayer := {
              playMode: -> if autoplay then "game" else "edit"
              fxNow: -> fxClock
              practiceFeedback: rollFeedback
              practiceHidesChannel: (c: number) => autoplay and not practiceSet.has c
              gridBeatOffset: -> beatOffset ?? 0
              practiceFingering: player.practiceFingering
              practiceJudgmentColors: player.practiceJudgmentColors
              rollBackground: player.rollBackground
              gameHudScore: hudScore
              gameHudCombo: hudCombo
              gameHudMultiplier: hudMultiplier
              gameHudHealth: hudHealth
              // rollInteraction wires pointer handlers at construction;
              // nothing dispatches events at an offscreen stage, but the
              // first guard it consults must exist
              paymentRequired: -> true
            } as! Player
            //@ts-ignore a Point quacks like the ObservablePoint the views read x/y from
            renderRoll = PianoRoll new Point(layoutWidth/2, layoutHeight/2), rollPlayer
            webmApp.stage.addChild renderRoll.container
            renderRoll.songLoaded song
            if autoplay
              //@ts-ignore
              renderHud = GameHudLayer new Point(layoutWidth/2, layoutHeight/2), rollPlayer
              webmApp.stage.addChild renderHud.container
          else
            renderCam = Camera
              //@ts-ignore
              new Point(layoutWidth/2, layoutHeight/2),
              mockPlayer as! Player
              {
                renderer: webmApp.renderer
                flipY: !!frameTexture
                loop: looping
                transparent: alpha
                scene: scene
                staffText: staffText ?? false
                poolScale: poolScale ?? 4
                loopSeconds: duration
                // The clip's own span, not song.beatLength(): startTime is 0 for a sliced
                // clip but not for a --start render of a whole song.
                loopBeats: song.beatAtTime(startTime + duration) - song.beatAtTime(startTime)
              }
            webmApp.stage.addChild renderCam.container

            renderCam.songLoaded song
            renderCam.resize()

        catch e
          //@ts-ignore
          Modal.alert e.message
          return Promise.reject e

        finally
          PIXI.settings.ROUND_PIXELS = roundPixelsWas

        // Two ways to get the frame out of the GPU.
        //
        // `toBlob` is asynchronous, and Chromium defers its callback to ~1Hz in a renderer
        // it considers hidden — measured at 995ms of every 1000ms per frame with the frame
        // loop itself dispatching 7ms apart. That makes an unattended render 50x slower
        // than the work it's doing, purely from being offscreen.
        //
        // `extract.pixels()` is a synchronous gl.readPixels, so nothing can defer it, and
        // it hands back raw RGBA that ffmpeg takes directly — skipping a PNG encode here
        // and a PNG decode there. Used whenever the caller is headless.
        // Read back through an explicit render texture rather than off the canvas.
        // extract.pixels() with no target computes its read region as
        // `renderer.width * resolution` — but renderer.width is ALREADY device pixels, so
        // at resolution 2 it asks gl.readPixels for four times the area that exists
        // (measured: 59MB per frame where the canvas holds 14.7MB, the rest read off the
        // end of the framebuffer). Given a texture it takes the frame and resolution from
        // the texture instead, which is exact at any scale.

        // The Application's backgroundColor clears the CANVAS; a render texture clears to
        // transparent, which came out of the encoder as black bars around the staff. Paint
        // the background into the scene instead, so both paths produce the same frame.
        // An alpha render leaves the texture's transparent clear in place — the whole
        // point is a frame with nothing behind the staff.
        if frameTexture and not alpha
          background := new Sprite Texture.WHITE
          background.tint = 0xEDE7F6
          background.width = layoutWidth
          background.height = layoutHeight
          // At index 0: the camera's container is already on the stage by this point, and
          // a background painted over the top of it is just a lavender rectangle.
          webmApp.stage.addChildAt background, 0

        doFrame := (t: number) ->
          probe and probe.mark = performance.now()
          beat := song.beatAtTime(t)
          if renderRoll
            fxClock = (t - startTime!) * 1000
            // Deliver this frame's perfect hits — score first, so the
            // feedback carries the tier the hit just earned, exactly as live
            // judging orders it in practice.civet
            while chartCursor < chart.length and chart[chartCursor].startTime <= t
              note := chart[chartCursor++]
              rollScore.judge "perfect"
              while rollFeedback.length and fxClock - rollFeedback[0].at > JUDGMENT_FEEDBACK_MS
                rollFeedback.shift()
              rollFeedback.push {
                n: note.n
                judgment: "perfect"
                beat: note.startBeat
                at: (note.startTime - startTime!) * 1000
                tier: rollScore.multiplier()
              }
            hudScore rollScore.score
            hudCombo rollScore.combo
            hudMultiplier rollScore.multiplier()
            hudHealth rollScore.health
            // the arcade pose clock rides the transport beat, same as the
            // live master render loop — offset so poses snap on the audible
            // downbeat grid when the song has a pickup
            setArcadeBeat beat - (beatOffset ?? 0)
            renderRoll.render song, beat
            renderHud?.render fxClock
          else
            renderCam!.render(song, beat)

          if frameTexture
            webmApp.renderer.render webmApp.stage, {renderTexture: frameTexture}
            probe and probe.render += performance.now() - probe.mark
            probe and probe.mark = performance.now()

            // gl.readPixels straight into our own buffer, rather than extract.pixels().
            // Extract runs arrayPostDivide over every pixel to undo premultiplied alpha —
            // 3.7M pixels of work per frame that these frames don't need, because the
            // background sprite makes them fully opaque.
            //
            // A ring of buffers, not one: frames stay in flight to the main process after
            // this returns, so reusing a single buffer would overwrite one still being
            // serialised.
            buffer := readBuffers[readIndex]
            readIndex = (readIndex + 1) % readBuffers.length

            renderer := webmApp.renderer as! PIXI.Renderer
            renderer.renderTexture.bind frameTexture
            {gl} := renderer
            gl.readPixels 0, 0, deviceWidth, deviceHeight, gl.RGBA, gl.UNSIGNED_BYTE, buffer
            renderer.renderTexture.bind null

            return Promise.resolve buffer

          webmApp.renderer.render(webmApp.stage)

          new Promise<Blob> (r, err) ->
            try
              webmApp.view.toBlob (blob) =>
                return r blob if blob
                err new Error "Failed to render frame"
            catch e
              err e

        return Promise.resolve()
        // Before anything is captured: an unloaded shader draws the flat lavender
        // placeholder, and in a render those frames are permanent — at the top of the
        // clip, which on a looping short is also the seam.
        .then ->
          presetLoaded vfxPresetName
        .then ->
          ipcRenderer.invoke 'startRender',
            bitRate: "128M"
            alpha: alpha
            alphaCodec: alphaCodec
            frameRate: fps
            title: title
            height: height
            upscale: upscale
            width: width
            outputPath: outputPath
            // ffmpeg needs the geometry up front for rawvideo — there are no frame
            // headers to infer it from, unlike a PNG stream. This is the DEVICE size: the
            // readback hands back the canvas, which is layout × scale.
            // A render texture comes back the opposite way up from a canvas readback, so
            // the vflip that path needs would put this one on its head.
            raw: raw and { width: deviceWidth, height: deviceHeight, flip: false }
            // Varispeed the mix onto the frame grid: reinterpret it at audioRate and
            // resample back, then pin the length so it is exactly as long as the video.
            // asetrate takes an integer, so the ratio is off by a fraction of a ppm and the
            // mix lands within a sample or two of the video rather than exactly on it. That
            // is left alone deliberately: padding to the sample means appending silence
            // (a click), trimming to it means dropping the last fraction of a millisecond
            // of music (a step at the seam — the thing this is all for). A sample of A/V
            // difference is 0.02ms and nothing can hear it.
            audio: stretched and {
              rate: round SAMPLE_RATE * duration! / videoSeconds
              sampleRate: SAMPLE_RATE
            }
        .then (res) ->
          renderId = res.renderId

          // Frames go straight down a FIFO when the main process made one, skipping the
          // IPC copy entirely — see renderMP4. Opening a FIFO for write blocks until a
          // reader attaches, and ffmpeg is already spawned reading it, so this resolves as
          // soon as it is up. No FIFO (Windows, or no mkfifo) means the IPC path below.
          if res.framePath and global.require
            fs := global.require 'fs'
            frameStream = fs.createWriteStream res.framePath
            frameStream.on 'drain', releaseWaiters
            frameStream.on 'error', (e: Error) ->
              streamError ?= e
              // Anything blocked on backpressure has to come loose, or the render hangs
              // instead of reporting the failure.
              releaseWaiters()

            // Bounded: opening a FIFO for write blocks until a reader attaches, so if
            // ffmpeg dies between spawning and opening its end, this would wait forever.
            await new Promise<void> (resolve, reject) ->
              timer := setTimeout ->
                reject new Error "timed out opening the frame pipe — ffmpeg never opened its end"
              , 15000
              frameStream.once 'open', ->
                clearTimeout timer
                resolve()
              frameStream.once 'error', (e: Error) ->
                clearTimeout timer
                reject e
          return
        .then =>
          // This is gnarly
          @renderOfflineContext({
            song: @song
            synth: @synth()
            progressView
            audioChannels: 2
            duration: duration
            frameCount
            loopTail
            fps
            sampleRate: SAMPLE_RATE
            startTime: startTime
            gain: gain ?? 1
            frameCallback: (t: number, analysedDestination: (typeof mockPlayer)["analysedDestination"]) ->
              mockPlayer.analysedDestination = untrimmedAnalyser analysedDestination as any, gain ?? 1

              // Where the wall clock goes, per stage. Nothing in this render saturates a
              // core — it's a serial chain of waits — so a total is useless and a
              // breakdown is the whole diagnosis. See --probe in render-cli.
              t0 := performance.now()
              probe and probe.wait += t0 - (probe.last or t0)

              frame := await doFrame(t)
              // raw mode already yields a Uint8Array; the blob path needs unwrapping
              bytes := if frame <? Blob
                new Uint8Array await frame.arrayBuffer()
              else
                frame

              t1 := performance.now()
              probe and probe.read += t1 - probe.mark

              // Hand the frame off WITHOUT waiting for it to arrive, keeping a few in
              // flight. Awaiting each one made the whole render serial — draw, ship, draw,
              // ship — and shipping is the slower half (measured 36.7ms against 24.4ms per
              // frame, with ffmpeg itself idling at 15% of the machine). With the two
              // overlapped the frame costs the slower of the pair, not the sum.
              //
              // Ordering is safe either way: one stream written in sequence, or one IPC
              // channel whose handler writes to stdin synchronously on arrival. The cap is
              // what keeps this from being an unbounded queue of 14.7MB frames.
              inFlight.push if frameStream
                writeFrame bytes
              else
                ipcRenderer.invoke 'renderFrame',
                  renderId: renderId
                  buffer: bytes

              if inFlight.length >= MAX_FRAMES_IN_FLIGHT
                await inFlight.shift()

              if probe
                t2 := performance.now()
                probe.frames++
                probe.draw += t1 - t0
                probe.ipc += t2 - t1
                probe.last = t2
              return
          })
        .then (channelData) ->
          // Every frame has to be through the pipe before ffmpeg is told there are no
          // more. Closing the FIFO is what gives ffmpeg its EOF.
          Promise.all(inFlight)
          .then ->
            return unless frameStream
            new Promise<void> (resolve) -> frameStream.end resolve
          .then -> channelData
        .then audioBufferToWav
        .then (blob) ->
          ipcRenderer.invoke 'renderFrame',
            renderId: renderId
            buffer: "done"
          .then ->
            blob.arrayBuffer()
          .then (buffer: ArrayBuffer) ->
            ipcRenderer.invoke 'audioBuffer',
              renderId: renderId
              buffer: new Uint8Array buffer
          .then (finalPath: string) ->
            console.log `RenderDIR: ${finalPath}`
            finalPath
        .finally ->
          frameTexture and frameTexture.destroy true
          renderCam?.dispose()
          renderRoll?.dispose()
          Modal.hide() unless silent
        .catch (e) ->
          // A headless caller (scripts/social) has no one to dismiss an alert and
          // needs the failure, not a swallowed one.
          throw e if silent
          Modal.alert e.message
          return undefined

  return Renderer

export default StaffRenderer

export type RendererInstance = InstanceType<ReturnType<typeof StaffRenderer>>

animate := (fn: =>) ->
  step := ->
    requestAnimationFrame(step)
    fn()

  step()

// `renderer` is the one this camera draws into, and it is NOT always the interactive
// app's: an offline video render has its own, at its own resolution. The vfx shaders take
// that resolution as a uniform, so reading it off the global app (which is what this used
// to do) fed them the screen's value while they ran at the render's — a mismatch nobody
// noticed while the two were both 1.
type CameraOptions = {
  // The renderer this camera draws into — NOT always the interactive app's: an offline
  // video render has its own, at its own resolution. The vfx shaders take that resolution
  // as a uniform.
  renderer?: PIXI.IRenderer
  // The frame is drawn into a render texture rather than onto the canvas, which is the
  // opposite way up — see the iFlipY uniform in shaders.civet.
  flipY?: boolean
  // Played on repeat: draw the song as a cylinder rather than a line (the lap loop in
  // render), and hide the start/end bar lines that would mark the seam.
  loop?: boolean
  // How long one lap is, in seconds and in quarter notes — the vfx shaders take both as
  // uniforms so their animation can be made to come back to its opening phase exactly
  // there. This is the clip's MUSICAL length, which is what iTime runs over: frames are
  // stepped at duration/frameCount, so the frame that follows the last one is iTime =
  // loopSeconds, not the video's own frameCount/fps.
  //
  // Not derived from the song inside Camera, even though a sliced clip is its own song,
  // because `duration` can be shorter than the song it was cut from and because a slice
  // loses any trailing rest — the song's length would then overstate the lap and every
  // shader would come back a fraction late.
  loopSeconds?: number
  loopBeats?: number
  // Alpha render: never attach the vfx backdrop filter, whatever preset the song names —
  // the frame behind the staff must stay transparent.
  transparent?: boolean
  // Live 3D backdrop, drawn under the staff in the vfx slot — see RenderScene. Gated by
  // `transparent` exactly like the vfx filter: an alpha plate must stay empty behind the
  // staff no matter what the caller wired up.
  scene?: RenderScene
  // Textual staff annotations: tempo markings, key/time signature labels, measure
  // numbers, markers, copyright, cue points, instrument names. On for the interactive
  // editor (they carry information you edit against); off by default in video renders
  // (renderVideo), where they read as clutter. Lyrics are content, not annotation, and
  // are not gated. Key-signature accidental GLYPHS are notation and stay too.
  staffText?: boolean
  // Multiplier on every sprite pool's cap (see the pools below). A pool size is a cap, not
  // a budget: a note that finds no free slot is not drawn, and it appears the moment the
  // scroll frees one — the sprite pop-in a long piece shows at 4K ultrawide, where the
  // frame holds several times as much of the staff at once as the 256/1024 defaults cover.
  // Raising it costs allocation and per-frame reset work whether or not the extra slots
  // get used, which a realtime editor pays for every frame and an offline render pays for
  // only in wall time — hence 1 here and a higher default in renderVideo.
  poolScale?: number
}

Camera := (screenHalfSize: ObservablePoint, player: Player, {renderer, flipY, loop: looping, loopSeconds, loopBeats, transparent, scene, staffText = true, poolScale = 1}: CameraOptions = {}) ->
  camera := new Container

  deferCleanup := [] as (()=>)[]

  BorderSlice := Texture.from "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAMAAAADCAYAAABWKLW/AAAAAXNSR0IArs4c6QAAABlJREFUGFdj/P///38GKGAEcRgZGSE0sgwAV3oP+Pv+qRoAAAAASUVORK5CYII="

  // Frequency and time domain data for shader
  // xyzw = FreqL, FreqR, TimeL, TimeR
  binCount := player.analysedDestination.l.frequencyBinCount
  frequencyBinDataL := new Uint8Array binCount
  frequencyBinDataR := new Uint8Array binCount
  timeDomainDataL := new Uint8Array binCount
  timeDomainDataR := new Uint8Array binCount
  // The waveform goes in phase-locked, so a scope drawn from it stands still on a held
  // note instead of sliding (lib/phase-lock.civet).
  lockScope := phaseLock binCount
  chan0TexBuffer := new Uint8Array binCount * 4
  iChannel0 := Texture.fromBuffer chan0TexBuffer, binCount, 1

  vfxShader := new Sprite Texture.WHITE
  vfxShader.width = 500
  vfxShader.height = 500
  vfxShader.filters = []
  camera.addChild vfxShader

  // The 3D backdrop rides in as a canvas-backed texture, directly above the vfx sprite
  // and below everything the staff draws. One texture upload per frame (baseTexture
  // .update() in render below) is the entire compositing cost — this layer replaces the
  // external pipeline that round-tripped the staff through a VP9 alpha plate into a
  // four-input ffmpeg graph to achieve the same stacking.
  let sceneSprite: Sprite?
  let sceneTexture: Texture?
  sceneFrame .= 0
  if scene
    sceneTexture = Texture.from scene.canvas
    sceneSprite = new Sprite sceneTexture
    camera.addChild sceneSprite
    deferCleanup.push -> scene.dispose?()

  cursorPosition: BeatNote? .= undefined
  let channelSprites: {[channel: number]: number}?

  // Color scheme
  lyricsColor .= 0x202020
  // Lyrics are the ONLY text-bearing event a render draws by default: MIDI text events
  // are off via showText, and marker/cuePoint/copyrightNotice/instrumentName/trackName
  // all sit behind staffText, which render-cli leaves false unless --staff-text. Lyrics
  // were gated on staffAlpha alone, so a plate rendered at a low staffAlpha to composite
  // over b-roll still burned karaoke text into the frame.
  lyricsVisible .= true

  textures .= [] as SpriteTexture[]

  textureFor := (channel: number, program: number, note: number, active?: boolean) ->
    i := channelSprites?[channel] ?? program
    t := textures[i % textures.length]

    if t
      if active
        t.active
      else
        t.normal

  // ledger lines
  linePool := ResourcePool 256 * poolScale, ->
    assign new Sprite(Texture.WHITE),
      anchor: center
      width: LEDGER_LINE_WIDTH
      height: 3
      tint: 0x000000

  // duration bars: gradient pills, see DurationBarPool
  durationPool := DurationBarPool 1024 * poolScale

  // travelling highlight over a sounding bar
  sheenPool := ResourcePool 64, ->
    assign new Sprite(SheenTexture),
      anchor: center

  spritePool := ResourcePool 1024 * poolScale, ->
    assign new Sprite(),
      anchor: center

  accidentalPool := ResourcePool 256 * poolScale, ->
    assign new Sprite(),
      anchor: center

  textPool := TextPool(256 * poolScale)
  measureNumberPool := MeasureNumberPool 16

  pools := [linePool, durationPool, sheenPool, spritePool, accidentalPool, textPool, measureNumberPool]

  // This only resests when redrawing the minimap, not every render
  minimapMarkerPool := TextPool 64,
    fontFamily: "monospace"
    fontSize: 12
    fill: 0xFFFFFF

  sharpSymbol := new Text "♯",
    fontFamily: "sans-serif"
    fontSize: 32
    fill: 0x000000
  sharpSymbol.updateText(false)

  flatSymbol := new Text "♭",
    fontFamily: "sans-serif"
    fontSize: 32
    fill: 0x000000
  flatSymbol.updateText(false)

  naturalSymbol := new Text "♮",
    fontFamily: "sans-serif"
    fontSize: 32
    fill: 0x000000
  naturalSymbol.updateText(false)

  playhead := new Sprite Texture.WHITE
  assign playhead,
    anchor: center
    width: 2
    alpha: 0.75
    tint: 0x673AB7

  prefaceBeats := 3.375

  updateMeasureNumbers := (beatStart: number, beatEnd: number) ->
    {x: shw, y: shh} := screenHalfSize

    player.song.measureNumbers.filter ({beat}) ->
      beatStart <= beat < beatEnd
    .forEach ({beat, n}) ->
      if s := measureNumberPool.getInstance((n + 1).toString())

        s.x = beat * PIXELS_PER_BEAT + 2
        s.y = shh - 7.5 * STAFF_SPACING - 16

        bgLayer.addChild s

  staff := new Container
  staff.x = -prefaceBeats * PIXELS_PER_BEAT
  staff.y = 0

  staffBg := new Sprite Texture.WHITE
  // staffBg.width = shw * 2
  staffBg.height = STAFF_SPACING * 14
  staffBg.y = -staffBg.height / 2
  staff.addChild staffBg

  trebleClefSprite := Sprite.from "https://danielx.net/composer/treble-clef.svg"
  trebleClefSprite.anchor = center
  trebleClefSprite.scale = center

  bassClefSprite := Sprite.from "https://danielx.net/composer/bass-clef.svg"
  bassClefSprite.anchor = center
  bassClefSprite.scale = center

  // clefs
  staff.addChild trebleClefSprite
  trebleClefSprite.x = 128
  trebleClefSprite.y = -130

  staff.addChild bassClefSprite
  bassClefSprite.x = 128
  bassClefSprite.y = 140

  grid := new Container
  grid.x = prefaceBeats * PIXELS_PER_BEAT
  // Grid lines
  [0...64].forEach (i) ->
    let alpha, width
    if i % 4 is 0
      alpha = 1
      width = 2
    else
      alpha = 0.25
      width = 1

    grid.addChild assign new Sprite(Texture.WHITE),
      anchor: center
      x: i * PIXELS_PER_BEAT
      width: width
      height: 10 * STAFF_SPACING
      tint: 0x000000
      alpha: alpha
  staff.addChild grid

  updateGridLines := (startBeat: number, endBeat: number, timeSignatures: { beat: number, numerator: number, denominator: number}[]) ->
    nextSignatureStart .= Infinity
    signatureStart .= 0
    numerator .= 4
    denominator .= 4
    let nextSignatureNumerator, nextSignatureDenominator

    startBeat = max 0, floor startBeat

    si .= 0
    tsl .= timeSignatures.length
    while si < tsl
      signature := timeSignatures[si]
      {beat} := signature
      if beat <= startBeat
        signatureStart = beat
        {numerator, denominator} = signature
      else
        nextSignatureStart = beat
        nextSignatureNumerator = signature.numerator
        nextSignatureDenominator = signature.denominator
        break
      si++

    {children:gridLines} := grid
    maxLines := gridLines.length

    // Quantize bar line based on signatureStart and step
    step .= 4/denominator
    i .= floor((startBeat - signatureStart) * step) / step + signatureStart

    n .= 0
    while n < maxLines and i < endBeat
      gridLine := gridLines[n] as Sprite
      gridLine.visible = i != 0
      gridLine.x = i * PIXELS_PER_BEAT

      step = 4 / denominator
      strong := ((i - signatureStart)/step) % numerator is 0

      if strong
        gridLine.alpha = 1
        gridLine.width = 2
        gridLine.anchor = center
      else
        gridLine.alpha = 0.25
        gridLine.width = 1
        gridLine.anchor = verticalCenter

      n++
      i += step

      // If we cross over a time signature
      if i >= nextSignatureStart
        i = signatureStart = nextSignatureStart
        numerator = nextSignatureNumerator!
        denominator = nextSignatureDenominator!
        si++

        if si < tsl
          {
            beat:nextSignatureStart
            numerator:nextSignatureNumerator
            denominator:nextSignatureDenominator
          } = timeSignatures[si]
        else
          nextSignatureStart = Infinity

    // Hide remaining lines
    while n < maxLines
      gridLine := gridLines[n]
      gridLine.visible = false
      n++

  lines := new Container
  staff.addChild lines

  // Staff lines
  [0...5].forEach (i) ->
    lines.addChild assign new Sprite(Texture.WHITE),
      y: -STAFF_SPACING * (i + 1) - 1.5
      height: 3
      tint: 0x000000

    lines.addChild assign new Sprite(Texture.WHITE),
      y: STAFF_SPACING * (i + 1) - 1.5
      height: 3
      tint: 0x000000

  staffBeginLine := assign new Sprite(Texture.WHITE),
    tint: 0x000000
    width: 3
    height: STAFF_SPACING * 10
    y: -STAFF_SPACING * 5
  staff.addChild staffBeginLine

  staffEndLine := assign new Sprite(Texture.WHITE),
    tint: 0x000000
    width: 3
    height: STAFF_SPACING * 10
    y: -STAFF_SPACING * 5
  staff.addChild staffEndLine

  // NOTE: `2` is correct for bottom and right size so that the transparent
  // middle pixel is included in the vertical and horizontal repeat, otherwise
  // things get a little weird. Update: this actually made it weird on my other
  // PC, 1.5 seems ok here though... wat.
  staffBorder := new NineSlicePlane BorderSlice, 1, 1, 1.5, 1.5
  staffBorder.x = 0
  // staffBorder.width = shw * 2
  staffBorder.height = STAFF_SPACING * 14
  staffBorder.y = -staffBorder.height / 2
  staffBorder.tint = 0x000000
  staffBorder.alpha = 0.5
  // staffBorder.anchor = center
  staff.addChild staffBorder
  // TODO: Handle resize of bgs and border on screen resize

  bottomShadow := new Sprite Texture.WHITE
  // bottomShadow.width = shw * 2
  bottomShadow.height = 1
  bottomShadow.tint = 0x000000
  bottomShadow.alpha = 0.25
  bottomShadow.y = STAFF_SPACING * 7
  staff.addChild bottomShadow

  viewport := new Container

  viewport.addChild staff

  bgLayer := new Container
  viewport.addChild bgLayer
  spriteLayer := new Container
  viewport.addChild spriteLayer
  fgLayer := new Container
  viewport.addChild fgLayer

  // Practice judgment popups. Its own layer: the three above are emptied
  // every render, these sprites persist and fade over several frames.
  practiceLayer := new Container
  viewport.addChild practiceLayer

  // combo-tier hit bursts at the note's staff position (see note-fx.civet).
  // Parented to the practice layer, so they scroll with the score. Normal
  // blending: additive sparks vanish against the staff's white field.
  practiceBursts := BurstLayer practiceLayer, additive: false
  practiceBurstsSeenThrough .= 0

  // big arcade judgment popups (arcade-fx). light: the staff's white field
  // kills additive flames, so they burn as opaque embers here
  practiceJudgmentFx := JudgmentFx practiceLayer, fontSize: 34, light: true

  // Judgments float up from the note's staff position as they fade. Wrong
  // notes anchor at the playhead's beat with the pitch they were played at.
  renderPracticeFeedback := ->
    feedbackList := player.practiceFeedback // absent on renderVideo's mockPlayer
    if feedbackList
      {y: shh} := screenHalfSize
      now := performance.now()
      // song-theme overrides (cached in the accessor)
      jc := player.practiceJudgmentColors?() ?? JUDGMENT_COLORS
      // game mode goes full arcade; practice keeps the look at a calmer size
      fxScale := if player.playMode?.() is "game" then 1 else 0.6
      feedbackList.forEach ({n, judgment, beat, at, tier}) ->
        age := now - at
        color := jc[judgment] ?? 0xFFFFFF
        {key} := player.song.keySignatureEventAtBeat beat
        [staffPosition] := midiPitchToStaffNote(n, key)
        noteY := shh - staffPosition * STAFF_SPACING / 2
        // a fresh hit not yet seen sparks at the note itself, sized by tier
        // (fresh: the cursor alone would replay stale entries when the view
        // starts rendering again). The echo wave rides in the judgment
        // color — white would vanish on the staff's light field.
        if at > practiceBurstsSeenThrough and age < BURST_MAX_AGE_MS
          spawnJudgedBurst practiceBursts, beat * PIXELS_PER_BEAT, noteY, color, judgment, tier, color
        return if age > JUDGMENT_FEEDBACK_MS
        practiceJudgmentFx.pop JUDGMENT_LABELS[judgment] ?? judgment, color, beat * PIXELS_PER_BEAT, noteY - 40, age, {tier, judgment, scale: fxScale}
      practiceBurstsSeenThrough = now
      practiceBursts.step now
    practiceJudgmentFx.finish performance.now()

  interactionLayer := new Sprite
  interactionLayer.width = 0x1000000

  eventAccidental := (player: Player, event: MouseEvent) ->
    {shiftKey, ctrlKey, metaKey} := event
    a .= player.accidentalModifier()

    if a is 0
      if shiftKey
        a = 1
      else if ctrlKey or metaKey
        a = -1
    else if a is 1 and (ctrlKey or metaKey)
      a = 0
    else if a is -1 and shiftKey
      a = 0

    return a

  screenPositionToBeatNote := (x: number, y: number, event: MouseEvent) ->
    {x: shw, y: shh} := screenHalfSize

    staffPosition := round (shh - y - yOffset) / (STAFF_SPACING / 2)

    beat := (x / (shw * 2) - 1 / 3) * 2 * shw / PIXELS_PER_BEAT + currentBeat

    {key} := player.song.keySignatureEventAtBeat(beat)

    beat: beat
    note: staffNoteToMidiPitch(staffPosition, eventAccidental(player, event), key)

  let toolHeld: Tool?
  interactionLayer.interactive = true
  interactionLayer.on "pointerdown", ({data}) ->
    return if player.paymentRequired()
    // Performing is play-only: input is judged, the canvas doesn't edit
    // (the edit chrome is hidden, so a live tool would only edit by accident)
    return unless player.playMode() is "edit"

    {global, originalEvent} := data
    {x, y} := global
    {which, pointerId} := originalEvent
    // Don't prevent default, it kills handling of pointerup event when cursor is
    // outside of page
    // originalEvent.preventDefault()

    if pointerId > 0
      document.body.setPointerCapture? pointerId

    beatNote := screenPositionToBeatNote(x, y, originalEvent)

    // Middle click to pan viewport
    if which is 2
      toolHeld = player.tools.pan
    // Right click to erase
    else if which is 3
      tool := player.activeTool()
      // some tools have a right click action that is specialized
      if tool.handlesRightClick
        toolHeld = tool
      else // erase is default right click action
        toolHeld = player.tools.erase
    else
      toolHeld = player.activeTool()

    toolHeld.down(player, beatNote, originalEvent)

  interactionLayer.on "pointermove", ({data}) ->
    {global, originalEvent} := data
    {x, y} := global

    {beat, note} .= cursorPosition = screenPositionToBeatNote(x, y, originalEvent)
    beat = quantize(max(beat, 0), player.quantize())
    time := player.song.timeAtBeat(beat)

    note = clamp note, 0, 127

    player.notePosition ```
      Time: ${time.toFixed(2)}s
      Beat: ${(beat + 1).toFixed(2)}
      Note: ${noteName note}
    ```

  pointerMoveHandler := (e: MouseEvent) ->
    if toolHeld
      // need position relative to viewport to account for toolbar height
      {pageX:x, pageY:y} := e
      {left:vx, top:vy} := player.viewportElement().getBoundingClientRect()

      beatNote := screenPositionToBeatNote(x, y - vy, e)
      toolHeld.move(player, beatNote, e)

  document.addEventListener "pointermove", pointerMoveHandler
  deferCleanup.push ->
    document.removeEventListener "pointermove", pointerMoveHandler

  pointerUpHandler := (e: MouseEvent) ->
    if toolHeld
      // need position relative to viewport to account for toolbar height
      {pageX:x, pageY:y} := e
      {left:vx, top:vy} := player.viewportElement().getBoundingClientRect()
      beatNote := screenPositionToBeatNote(x, y - vy, e)
      toolHeld.up(player, beatNote, e)
      toolHeld = undefined

  document.addEventListener "pointerup", pointerUpHandler
  deferCleanup.push ->
    document.removeEventListener "pointerup", pointerUpHandler

  viewport.addChild interactionLayer
  camera.addChild viewport

  // Staff in world space pixels
  resizeStaff := (songBeatLength: number) ->
    // Looping draws the song three times side by side, so the strip spans all three. The
    // notes wrap per-event, but the background and staff lines are one sprite each and at
    // a single song length stop dead at the seam.
    songSpan := songBeatLength * PIXELS_PER_BEAT
    x0 := if looping then -songSpan else 0
    width := (prefaceBeats + songBeatLength) * PIXELS_PER_BEAT + (if looping then 2 * songSpan else 0)

    bottomShadow.x = staffBg.x = staffBorder.x = x0
    bottomShadow.width = staffBg.width = staffBorder.width = width
    staffEndLine.x = x0 + width - 3

    lines.children.forEach (line) ->
      //@ts-ignore
      line.x = x0
      //@ts-ignore
      line.width = width

  player.song.beatLength.observe (songBeatLength: number) ->
    resizeStaff songBeatLength
    redrawMinimap()

  player.on "changed", ->
    redrawMinimap()

  camera.addChild playhead

  // Minimap
  minimap := new Container
  minimapBg := new Graphics
  minimapBg.interactive = true
  movingMinimap .= false
  moveMinimap := (data: { global: x: number }) ->
    {x:shw, y:shh} := screenHalfSize
    mmshw := minimapScreen.width / 2

    x .= (data.global.x - mmshw) / (2 * shw - 2 * mmshw)
    x = clamp x, 0, 1
    l := player.song.beatLength()

    player.currentBeat l * x

  mouseUpHandler := ->
    movingMinimap = false

  document.addEventListener "mouseup", mouseUpHandler

  deferCleanup.push ->
    document.removeEventListener "mouseup", mouseUpHandler

  minimapBg.on "pointermove", ({data}) ->
    if movingMinimap
      moveMinimap(data)
  minimapBg.on "pointerdown", ({data}) ->
    movingMinimap = true
    moveMinimap(data)

  minimap.addChild minimapBg

  minimapScreen := new Sprite Texture.WHITE
  minimapScreen.alpha = 0.5
  minimapScreen.height = MINIMAP_HEIGHT
  minimap.addChild minimapScreen

  camera.addChild minimap

  // update when song is loaded or config changes
  ticksPerBeat .= 24

  // theming
  showMeasureNumbers .= true
  // these will connect to display settings later
  showText .= false // don't show MIDI Text events
  staffAlpha .= 1

  // update every render
  currentBeat .= 0
  drawVelocities .= true
  screenWidthPixels .= 0
  screenWidthBeats .= screenWidthPixels / PIXELS_PER_BEAT
  screenStartBeat .= 0
  screenEndBeat .= 0
  yOffset .= 0 // pixels
  spriteScale .= 1
  isPlaying .= false
  noteStartMap := new Map
  lyricsPos .=
    beat: -1
    y: 16
  lyricsLineHeight .= 32
  channelPrograms: number[] .= Array(16).fill(0)

  // This is updated during rendering based on midi key signature events and
  // used in noteOn/Off events to render sharps or flats based on key signature
  currentKeySignature .= 0
  // the song's fingering annotations, refreshed each render
  let cameraFingering: Record<string, string>?

  // Beats added to every event's position for the current lap: 0 for the song itself, ±one
  // song length for the wrapped copies. Everything downstream takes its x from `beat`, so
  // shifting here moves a whole lap and nothing else.
  beatOffset .= 0

  // The widest off-screen slack any branch allows (notes and text use 20 beats, the rest
  // less). A lap outside it can't contribute a visible sprite.
  LAP_SLACK := 20

  // Which copies of the song are on screen — just the song itself unless this camera loops
  // and a neighbour actually reaches the window, which is only near the seam.
  laps := (songBeatLength: number) ->
    return [0] unless looping and songBeatLength > 0

    [-1, 0, 1].filter (lap: number) ->
      lapStart := lap * songBeatLength
      lapStart + songBeatLength >= screenStartBeat - LAP_SLACK and
        lapStart <= screenEndBeat + LAP_SLACK

  // Render the duration bar, adjust the start note position and animations
  renderNoteOff := (c: number, n: number, beat: number, noteStart: [number, number, number]) ->
    {x:shw, y: shh} := screenHalfSize

    [staffPosition, accidental] := midiPitchToStaffNote(n, currentKeySignature)

    if durationBar := durationPool.getInstance()
      y := floor shh - staffPosition * STAFF_SPACING / 2
      // nine-slice planes have no anchor: position by top-left
      durationBar.y = y - DURATION_BAR_HEIGHT / 2

      let b: number, velocity: number
      program .= 0

      if noteStart?
        [b, velocity, program] = noteStart
      else
        b = beat - screenWidthBeats
        velocity = 0x40 / 0x7f

      onX := floor b * PIXELS_PER_BEAT
      width := floor (beat - b) * PIXELS_PER_BEAT
      // below the slice ends' combined width the corners would fold over
      durationBar.width = max width, 2 * DURATION_BAR_RADIUS
      durationBar.x = onX
      durationBar.tint = ChannelColors[c]

      let texture, s
      if texture = textureFor(c, program, n)
        if s = spritePool.getInstance()
          s.texture = texture
          s.alpha = 1
          s.x = onX
          s.y = y
          s.width = texture.width * spriteScale
          s.height = texture.height * spriteScale

          spriteLayer.addChild s

      // Slide along with playhead
      if s and isPlaying
        px := currentBeat * PIXELS_PER_BEAT
        // jump up on play
        dy .= 0
        jx := PIXELS_PER_BEAT / 4

        if onX <= px < onX + max(2 * jx, width)
          s.texture = textureFor(c, program, n, true)!

          if px < onX + jx
            dy = -8

        s.y = y + dy
        s.x = clamp px, onX, onX + width


      // Display velocity bar
      let vs
      if drawVelocities and vs = spritePool.getInstance()
        vs.texture = velocityTextures[floor(velocity * 127)]
        vs.alpha = 1

        // TODO better match velocity bar to sprite size?
        vs.x = onX + (texture?.width ?? SPRITE_SIZE) / 2
        vs.y = y + SPRITE_SIZE / 2 - velocity * SPRITE_SIZE / 2
        vs.width = 4
        vs.height = velocity * SPRITE_SIZE

        fgLayer.addChild vs

      bgLayer.addChild durationBar

      // travelling highlight: a sheen sweeps the bar while the note sounds
      // (added after the bar so it draws over it; independent of the
      // character sprite riding it)
      if isPlaying
        px := currentBeat * PIXELS_PER_BEAT
        if onX <= px < onX + width
          if sheen := sheenPool.getInstance()
            sheen.x = px
            sheen.y = y
            sheen.width = DURATION_BAR_HEIGHT * 3
            sheen.height = DURATION_BAR_HEIGHT * 1.5
            sheen.alpha = 0.9
            bgLayer.addChild sheen

  renderEventHandler := do
    tick .= 0
    beat .= 0

    (e: MidiEvent) =>
      { tick: t, type } := e
      tick = t
      beat = tick / ticksPerBeat + beatOffset

      switch type
        when "copyrightNotice"
          return unless staffText
          {x:shw, y: shh} := screenHalfSize

          // Skip if staff is hidden
          return unless staffAlpha

          // skip if off screen
          if !(screenStartBeat - 20 <= beat < screenEndBeat + 20)
            return

          { text } := e

          if t := textPool.getTextSprite(text.replace(/;/g, "\n"))
            t.y = shh + 6 * STAFF_SPACING
            t.x = floor beat * PIXELS_PER_BEAT
            t.tint = 0x808080
            centerSprite t

            fgLayer.addChild t

        when "cuePoint"
          return unless staffText
          {x: shw, y: shh} := screenHalfSize

          // Skip if staff is hidden
          return unless staffAlpha

          // skip if off screen
          if !(screenStartBeat - 20 <= beat < screenEndBeat + 20)
            return

          { text } := e

          if t := textPool.getTextSprite(text)
            t.y = 2 * shh - 64 - MINIMAP_HEIGHT
            t.x = floor beat * PIXELS_PER_BEAT
            t.tint = 0x008000

            fgLayer.addChild t

        when "lyrics"
          return unless lyricsVisible
          // Skip if staff is hidden
          return unless staffAlpha

          // skip if off screen
          if !(screenStartBeat - 20 <= beat < screenEndBeat + 20)
            return

          if beat > lyricsPos.beat
            lyricsPos.beat = beat
            lyricsPos.y = 64

          { text } := e
          if t := textPool.getTextSprite(text)
            t.y = lyricsPos.y
            lyricsPos.y += lyricsLineHeight
            t.x = floor beat * PIXELS_PER_BEAT
            t.tint = lyricsColor
            centerSprite t

            fgLayer.addChild t

        when "marker"
          return unless staffText
          {x: shw, y: shh} := screenHalfSize

          // Skip if staff is hidden
          return unless staffAlpha

          // skip if off screen
          if !(screenStartBeat - 20 <= beat < screenEndBeat + 20)
            return

          { text } := e
          if t := textPool.getTextSprite(text)
            t.y = shh + 5.5 * STAFF_SPACING
            t.x = floor t.width / 2 + beat * PIXELS_PER_BEAT
            t.tint = 0x202020

            fgLayer.addChild t

        when "instrumentName"
          return unless staffText
          // Skip if staff is hidden
          return unless staffAlpha

          // skip if off screen
          if !(screenStartBeat - 20 <= beat < screenEndBeat + 20)
            return

          { text } := e
          if t := textPool.getTextSprite(text)
            t.y = 16
            t.x = t.width / 2 + floor beat * PIXELS_PER_BEAT
            t.tint = 0x202020
            centerSprite t

            fgLayer.addChild t

        when "trackName"
          // Don't display track names
          return

          // skip if off screen
          if !(screenStartBeat - 20 <= beat < screenEndBeat + 20)
            return

          { text } := e
          if t := textPool.getTextSprite(text)
            t.y = 16
            t.x = t.width / 2 + floor beat * PIXELS_PER_BEAT
            t.tint = 0x202020

            fgLayer.addChild t

        when "text"
          return unless showText
          // Skip if staff is hidden
          return unless staffAlpha

          // skip if off screen
          if !(screenStartBeat - 20 <= beat < screenEndBeat + 20)
            return

          { text } := e
          if t := textPool.getTextSprite(text)
            t.y = 16
            t.x = floor beat * PIXELS_PER_BEAT
            t.tint = 0x808080
            centerSprite t

            fgLayer.addChild t

        when "keySignature"
          // Skip if staff is hidden
          return unless staffAlpha

          { key: n, scale: m } .= e

          // TODO: need to fix midi note to staff position function first
          currentKeySignature = n

          // skip if off screen
          if !(screenStartBeat - 1 <= beat < screenEndBeat + 1)
            return

          {x: shw, y: shh} := screenHalfSize

          x := floor beat * PIXELS_PER_BEAT

          if staffText
            if t := textPool.getTextSprite(midiKeySignatureName(n, m))
              t.y = shh - 6 * STAFF_SPACING - t.height / 2
              t.x = x - t.width / 2
              t.tint = 0x202020

              fgLayer.addChild t

          // Add signature accidentals
          i .= 0
          while i < n
            if a := accidentalPool.getInstance()
              p .= (5 - 1.5 * i)
              while p <= 2
                p += 3.5
              a.texture = sharpSymbol.texture
              a.x = x + 12 * i
              a.y = shh - (p % 6.5) * STAFF_SPACING
              fgLayer.addChild a

            i++

          if n < 0
            n = -n
            while i < n
              if a := accidentalPool.getInstance()
                p .= (3 + 1.5 * i)
                while p > 5
                  p -= 3.5
                a.texture = flatSymbol.texture
                a.x = x + 12 * i
                a.y = shh - (p % 6.5) * STAFF_SPACING
                fgLayer.addChild a
              i++

        when "timeSignature"
          return unless staffText
          // Skip if staff is hidden
          return unless staffAlpha

          {x:shw, y: shh} := screenHalfSize

          // skip if off screen
          if !(screenStartBeat - 1 <= beat < screenEndBeat + 1)
            return

          { numerator, denominator } := e

          if t := textPool.getTextSprite(`${numerator}/${denominator}`)
            // Centered manually because scrolling and using anchor 0.5,0.5 causes bouncing for non-integer widths
            t.y = shh - 5.5 * STAFF_SPACING - t.height / 2
            t.x = floor beat * PIXELS_PER_BEAT - t.width / 2
            t.tint = 0x202020

            fgLayer.addChild t

          return

        // Handle note off event, look up start note in map
        when "noteOff"
          { c, n } := e
          k := channelNoteKey(c, n)
          noteStarts := noteStartMap.get(k)

          if noteStarts and noteStarts.length
            // need to close start even if we don't render to accurately track held notes
            noteStart := noteStarts.shift()
            [startBeat] := noteStart
            // skip if off screen
            if !overlap(startBeat, beat, screenStartBeat - 1, screenEndBeat + 1)
              return

            renderNoteOff(c, n, beat, noteStart)
          else
            ; // TODO: excess note off

        when "noteOn"
          {x: shw, y: shh} := screenHalfSize
          { c, n, v } := e

          // Track note starts
          k := channelNoteKey(c, n)
          noteStarts .= noteStartMap.get(k)

          noteStart := [beat, v / 127, channelPrograms[c]]

          if noteStarts
            noteStarts.push noteStart
          else
            noteStarts = [noteStart]
            noteStartMap.set(k, noteStarts)

          // skip if off screen
          if !(screenStartBeat - 1 <= beat < screenEndBeat + 1)
            return

          [staffPosition, accidental] := midiPitchToStaffNote(n, currentKeySignature)

          x := floor beat * PIXELS_PER_BEAT
          y := floor shh - staffPosition * STAFF_SPACING / 2

          switch staffPosition
            when -12, 0, 12
              // ledger line
              if line := linePool.getInstance()
                line.x = x
                line.y = y

                bgLayer.addChild line

          // Add accidentals
          let a
          switch accidental
            when Accidental.Sharp
              if a = accidentalPool.getInstance()
                a.texture = sharpSymbol.texture
            when Accidental.Flat
              if a = accidentalPool.getInstance()
                a.texture = flatSymbol.texture
            when Accidental.Natural
              if a = accidentalPool.getInstance()
                a.texture = naturalSymbol.texture

          if a
            a.x = x - 24
            a.y = y - 12
            fgLayer.addChild a

          // Fingering above the notehead, printed-music style
          if cameraFingering
            if hint := cameraFingering[`${tick}:${c}:${n}`]
              {text: fingerText, hand} := fingeringDisplay hint
              if fs := textPool.getTextSprite(fingerText)
                fs.tint = FINGERING_COLORS[hand] ?? 0x202020
                fs.x = x
                fs.y = y - STAFF_SPACING - 8
                centerSprite fs
                fgLayer.addChild fs

        when "pitchBend"
          // convert 14-bit pitchBend value to [-1, 1]
          // v = v / 8192 - 1
          // hh := MINIMAP_HEIGHT / 2

          // x := screenWidthPixels * (beat - screenStartBeat) / (screenEndBeat - screenStartBeat)

          // TODO: Control graphics
          // controlGraphics.beginFill 0xFF00FF
          // .drawRect x, hh, 1, -hh * v
          // .endFill()

        when "programChange"
          { c, program: p } := e
          // Remember program per channel
          channelPrograms[c] = p

          return // skip for now

          {x: shw, y: shh} := screenHalfSize

          // skip if off screen
          if !(screenStartBeat - 1 <= beat < screenEndBeat + 1)
            return

          if t := textPool.getTextSprite(`P:${p}`)
            t.y = shh - (6 - c/2) * STAFF_SPACING
            t.x = floor beat * PIXELS_PER_BEAT
            t.tint = 0x00A000

            fgLayer.addChild t

        when "setTempo"
          return unless staffText
          // Skip if staff is hidden
          return unless staffAlpha

          { microsecondsPerBeat } := e

          bpm := 60000000 / microsecondsPerBeat

          // skip if off screen
          if !(screenStartBeat - 1 <= beat < screenEndBeat + 1)
            return

          {x: shw, y: shh} := screenHalfSize

          if t := textPool.getTextSprite((floor(bpm * 32) / 32).toFixed(0) + " bpm")
            t.y = shh - 6.5 * STAFF_SPACING - t.height / 2
            t.x = floor beat * PIXELS_PER_BEAT - t.width / 2
            t.tint = 0x202020

            fgLayer.addChild t

  redrawMinimap := ->
    {x:shw, y:shh} := screenHalfSize

    screenWidthPixels = 2 * shw

    startPaddingBeats := (screenWidthPixels / 3) / PIXELS_PER_BEAT
    endPaddingBeats := (2 * screenWidthPixels / 3) / PIXELS_PER_BEAT

    song := player.song
    songBeatLength := song.beatLength()
    noteStarts := new Map

    minimapMarkerPool.reset()

    minimapBg.clear()
    minimapBg.beginFill(0x666666, 0.875)
    .drawRect(0, 0, screenWidthPixels, MINIMAP_HEIGHT)
    .endFill()

    minimapBeatPosition := (b: number) ->
      floor screenWidthPixels * (b + startPaddingBeats) / (songBeatLength + startPaddingBeats + endPaddingBeats)

    minimapEventHandler := do
      tick .= 0
      beat .= 0

      (e: MidiEvent) =>
        { tick: t, type } := e

        tick = t
        beat = tick / ticksPerBeat

        switch type
          when "marker"
            x := minimapBeatPosition beat

            { text } := e

            // Drop shadow
            if s := minimapMarkerPool.getTextSprite(text)
              s.anchor = zeroPoint
              s.tint = 0x000000
              s.x = x + 1
              s.y = 3

              minimapBg.addChild s

            // White text
            if s := minimapMarkerPool.getTextSprite(text)
              s.anchor = zeroPoint
              s.tint = 0xFFFFFF
              s.x = x
              s.y = 2

              minimapBg.addChild s

          when "noteOn"
            { c, n } := e
            k := channelNoteKey(c, n)

            starts := noteStarts.get(k)

            if starts
              starts.push beat
            else
              noteStarts.set(k, [beat])

          when "noteOff"
            { c, n } := e
            k := channelNoteKey(c, n)
            starts := noteStarts.get(k)

            let b
            if starts and (b = starts.shift())?
              x := minimapBeatPosition b
              width := max 1, ceil(screenWidthPixels * (beat - b) / songBeatLength)
              y := floor MINIMAP_HEIGHT * (127 - n) / 128

              // console.log c, n, x, y, width

              minimapBg.beginFill(ChannelColors[c])
              .drawRect(x, y, width, 1)
              .endFill()
            else
              console.warn "excess note off", e

    song.events.forEach (event) -> minimapEventHandler event

    minimap.y = shh * 2 - MINIMAP_HEIGHT

  renderSelectionPreview := ->
    pattern := player.activePattern()
    if cursorPosition and pattern
      {beat:b, note:n} .= cursorPosition
      {x: shw, y: shh} := screenHalfSize

      b = quantize b, player.quantize()

      {channelPrograms, events, ticksPerBeat:ptpb} := pattern

      events.forEach (e: MidiEvent) ->
        {channel, note, tick, type} := e
        return unless type is "noteOn" and channel? and note?

        noteNum := note - 60 + n
        beat := b + tick / ptpb

        if texture := textureFor(channel, channelPrograms[channel], noteNum)
          if s := spritePool.getInstance()
            // TODO: account for key signature
            [staffPosition, accidental] := midiPitchToStaffNote(noteNum, 0)

            s.texture = texture
            s.alpha = 0.75
            s.x = floor beat * PIXELS_PER_BEAT
            s.y = floor shh - staffPosition * STAFF_SPACING / 2
            s.width = texture.width * spriteScale
            s.height = texture.height * spriteScale

            spriteLayer.addChild s

  container: camera
  render: (song: MidiSong, _currentBeat: number) ->
    currentBeat = _currentBeat
    isPlaying = player.playing()
    songBeatLength := song.beatLength()
    ticksPerBeat = song.ticksPerBeat
    textures = player.spriteTextures()
    spriteScale = player.spriteScale()
    channelActive := song.channelActive
    channelSprites = song.channelSprites()
    drawVelocities = player.velocityToolActive()
    cameraFingering = player.practiceFingering?.()
    // adjust viewport position when tracking playhead
    {x:shw, y: shh} := screenHalfSize
    {y:yOffset} = player.viewportOffset()

    // Shader stuff
    //@ts-expect-error
    {themeKey, filter} := presetByName(player.vfxPresetName())

    // @ts-ignore
    theme := song.theme() or THEMES[themeKey] or THEMES.light

    if filter and not transparent // vfx filter
      vfxShader.width = 2 * shw
      vfxShader.height = 2 * shh
      vfxShader.filters![0] = filter

      // Frequency spectrum data
      player.analysedDestination.l.getByteFrequencyData(frequencyBinDataL)
      player.analysedDestination.r.getByteFrequencyData(frequencyBinDataR)
      // The long waveform-only pair where there is one (lib/fx.civet); stand-ins without it
      // lock the spectrum pair's own shorter buffer.
      {scope, l: al, r: ar} := player.analysedDestination as any
      lockScope scope?.l ?? al, scope?.r ?? ar, timeDomainDataL, timeDomainDataR

      l := frequencyBinDataL.length
      for i of [0...l]
        b := i * 4
        chan0TexBuffer[b+0] = frequencyBinDataL[i]
        chan0TexBuffer[b+1] = frequencyBinDataR[i]
        chan0TexBuffer[b+2] = timeDomainDataL[i]
        chan0TexBuffer[b+3] = timeDomainDataR[i]
      iChannel0.update()

      filter.uniforms.iBeat = currentBeat
      filter.uniforms.iTime = song.timeAtBeat(currentBeat)
      // 0 during ordinary playback, which never comes back around. See loopRate/loopDrift
      // in shaders.civet for what a shader does with these.
      filter.uniforms.iLoopTime = if looping then loopSeconds ?? 0 else 0
      filter.uniforms.iLoopBeats = if looping then loopBeats ?? 0 else 0
      filter.uniforms.iChannel0 = iChannel0
      // Device pixels, matching gl_FragCoord — a filter renders into a framebuffer sized
      // by the renderer's resolution, so at scale 2 a layout-pixel iResolution would have
      // every `fragCoord/iResolution.xy` in the set running 0..2 instead of 0..1.
      resolution := renderer?.resolution ?? app.renderer.resolution
      filter.uniforms.iResolution[0] = vfxShader.width * resolution
      filter.uniforms.iResolution[1] = vfxShader.height * resolution
      filter.uniforms.iResolution[2] = resolution
      filter.uniforms.iFlipY = if flipY then 1 else 0
    else
      vfxShader.width = 0
      vfxShader.height = 0
      vfxShader.filters!.length = 0

    if scene and not transparent
      // Same instant the staff and the vfx uniforms get: exact musical time from the
      // tempo map, and the analyser suspended at this frame. The frame counter is local
      // and monotonic — scenes use it for per-frame effects (grain seeds), not timing.
      scene.drawFrame
        frame: sceneFrame++
        time: song.timeAtBeat(currentBeat)
        beat: currentBeat
        loopBeats: loopBeats ?? 0
        analyser: player.analysedDestination
      sceneSprite!.width = 2 * shw
      sceneSprite!.height = 2 * shh
      sceneTexture!.baseTexture.update()

    // theming
    staffAlpha = theme.staffAlpha ?? 1

    // Colors / Theme
    staffBorder.alpha = 0.5
    lyricsColor = theme.lyricsColor
    lyricsVisible = theme.lyricsVisible ?? true
    // In a loop these mark where the clip is in the song, which is exactly what a
    // seamless repeat must not show — same reasoning as the begin/end lines below.
    showMeasureNumbers = !looping and staffText and (theme.showMeasureNumbers ?? true)
    playheadVisible := theme.playheadVisible ?? true
    bottomShadow.alpha = 0.25
    staff.alpha = staffAlpha
    // The paper, separately from the lines: over a live scene even 0.125 of paper reads
    // as a grey band ruling the frame, while the lines at the same alpha are what keeps
    // the staff legible. The accepted DanVancouver look is lines with no paper —
    // staffPaperAlpha: 0 — and 1 keeps every existing render identical.
    staffBg.alpha = theme.staffPaperAlpha ?? 1

    // Reset lyrics spacer state
    lyricsPos.beat = -1

    screenWidthPixels = shw * 2
    screenWidthBeats = screenWidthPixels / PIXELS_PER_BEAT

    playhead.x = 2 * shw / 3
    playhead.y = shh - yOffset
    playhead.height = 14 * STAFF_SPACING
    playhead.visible = playheadVisible

    // Minimap screen width is same proportion as screen width beats to song length beats including one screen of padding
    minimapScreen.width = ceil screenWidthPixels * screenWidthBeats / (songBeatLength + screenWidthBeats)
    minimapScreenPosition := currentBeat / songBeatLength * (screenWidthPixels - minimapScreen.width)
    minimapScreen.x = clamp minimapScreenPosition, 0, screenWidthPixels - minimapScreen.width
    minimap.visible = !looping and (theme.minimapVisible ?? true)

    // In a loop these mark the seam, not the song's edges: past them is the same music.
    staffBeginLine.visible = staffEndLine.visible = !looping

    // Snap the scroll to a whole DEVICE pixel.
    //
    // Two things in the scene disagree about where a pixel is. Sprite/Text snap their
    // vertices when ROUND_PIXELS is set — to whole *layout* pixels, because PIXI 6.1.2
    // rounds against the global settings.RESOLUTION rather than the renderer's, and
    // truncates besides. NineSlicePlane is a Mesh and doesn't snap at all. So a staff or
    // measure-number border tracked the true scroll while the sprites inside it moved in
    // 2-device-pixel jumps, and the border shifted against its own contents every frame —
    // which is the flicker on their left and right edges.
    //
    // Snapping here puts everything on one grid: the meshes land on device pixels, and the
    // sprites' own rounding becomes a no-op on top of an already-integer offset (every
    // other x in the staff is a whole number — PIXELS_PER_BEAT is 96, prefaceBeats*96 is
    // 324). It also halves the judder that quantisation causes: measured 6,6,6,6,8 device
    // pixels per frame before, 6,6,7 after, for a scroll whose true speed is 6.4.
    snap := renderer?.resolution ?? 1
    viewport.x = round((2 / 3 * shw - currentBeat * PIXELS_PER_BEAT) * snap) / snap
    viewport.y = -yOffset
    interactionLayer.x = -2/3 * shw
    interactionLayer.height = shh * 2
    interactionLayer.y = yOffset

    screenStartBeat = currentBeat - screenWidthBeats / 3
    screenEndBeat = currentBeat + 2 * screenWidthBeats / 3

    bgLayer.removeChildren()
    spriteLayer.removeChildren()
    fgLayer.removeChildren()

    // Clamped to the song's length normally, so the staff stops where the music does; a
    // loop is a cylinder, so the lines carry on across the seam with the notes.
    gridEnd := if looping then screenEndBeat + 0.125 else min(songBeatLength, screenEndBeat + 0.125)
    updateGridLines(screenStartBeat - 0.125, gridEnd, song.timeSignatures())

    // Reset pools
    pools.forEach (pool) ->
      pool.reset()

    // Once per visible lap, so a looping staff has no edge: -1 is the song's end drawn
    // before its beginning, +1 its beginning past its end, each a whole song length away.
    //
    // The WHOLE list per lap, with a clean map: the handler drains `noteStartMap` as a FIFO
    // per (channel, note) and closes starts even when it doesn't draw them, so a partial
    // walk unbalances it and every later noteOff pairs with the wrong start.
    // hoisted out of the per-event walk below: practiceHidesChannel reads
    // an Observable, and per event per frame that dominates large songs
    drawChannel := [0...16].map (c) =>
      channelActive[c] and not player.practiceHidesChannel?.(c)

    for lap of laps(songBeatLength)
      beatOffset = lap * songBeatLength
      noteStartMap.clear()

      song.events.forEach (event) ->
        c := event.channel
        if !c? or drawChannel[c]
          renderEventHandler event

    beatOffset = 0
    noteStartMap.clear()
    if showMeasureNumbers
      updateMeasureNumbers(screenStartBeat - 1, screenEndBeat + 1)

    // Render selection preview
    renderSelectionPreview()

    renderPracticeFeedback()

  resize: ->
    {x:shw, y:shh} := screenHalfSize
    staff.y = shh
    redrawMinimap()

    if shh < 480
      MINIMAP_HEIGHT = 16
    else
      MINIMAP_HEIGHT = 128

    return

  screenPositionToBeatNote: screenPositionToBeatNote

  songLoaded: (song: MidiSong) ->
    ticksPerBeat = song.ticksPerBeat
    resizeStaff(song.beatLength())

    redrawMinimap()
    return

  createPatternPreview: (pattern: Pattern) ->
    width := 128
    height := 96

    renderer := new Renderer
      backgroundAlpha: 0
      width: width
      height: height

    { events, channelPrograms } .= pattern
    // copy program mapping
    channelPrograms = channelPrograms.slice()

    stage := new Container
    scale := new Point 1/2, 1/2 |> as ObservablePoint

    events.forEach (e: MidiEvent) ->
      {type} := e

      switch type
        when "noteOn"
          { channel, note, tick } := e
          return unless channel? and note?

          sprite := new Sprite textureFor(channel, channelPrograms[channel], note)
          sprite.x = (sprite.width / 2 + tick * PIXELS_PER_BEAT / ticksPerBeat) * scale.x
          // TODO: Accidental based on key signature
          [staffPosition, accidental] := midiPitchToStaffNote(note, 0)
          sprite.y = height / 2 - staffPosition * scale.y * STAFF_SPACING / 2
          sprite.scale = scale
          sprite.anchor = center
          stage.addChild sprite
        when "programChange"
          { channel, program } := e
          return unless channel? and program?

          channelPrograms[channel] = program

    renderer.render stage,
      clear: true

    return renderer.view

  dispose: ->
    deferCleanup.forEach (fn) -> fn()
